import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createGrantOpportunityComment,
  listGrantOpportunityComments,
} from "@/api/grant_opportunity_comments";
import { CommentThread } from "@/components/comments/CommentThread";
import { groupComments } from "@/components/comments/groupComments";
import { GrantOpportunitySection } from "@/components/grant_suite/GrantOpportunitySection";
import { invalidateGrantSuitePipeline } from "@/lib/grantSuiteQueryKeys";

type GrantOpportunityCommentsProps = {
  municipalityId: number;
  opportunityId: number;
  embedded?: boolean;
};

export function groupGrantOpportunityComments(
  comments: Parameters<typeof groupComments>[0],
) {
  return groupComments(comments);
}

export function GrantOpportunityComments({
  municipalityId,
  opportunityId,
  embedded = false,
}: GrantOpportunityCommentsProps) {
  const queryClient = useQueryClient();
  const queryKey = ["grant_opportunity_comments", municipalityId, opportunityId] as const;

  const { data: comments = [], isLoading, error } = useQuery({
    queryKey,
    queryFn: () => listGrantOpportunityComments(municipalityId, opportunityId),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey });
    invalidateGrantSuitePipeline(queryClient, municipalityId);
  };

  const createMutation = useMutation({
    mutationFn: ({ body, parentId }: { body: string; parentId?: number | null }) =>
      createGrantOpportunityComment(municipalityId, opportunityId, body, parentId),
    onSuccess: () => {
      invalidate();
    },
  });

  const thread = (
    <CommentThread
      comments={comments}
      isLoading={isLoading}
      error={Boolean(error)}
      submitting={createMutation.isPending}
      onCreateComment={async (body, parentId) => {
        await createMutation.mutateAsync({ body, parentId });
      }}
      testId="grant-opportunity-comments"
    />
  );

  if (embedded) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Team discussion visible to everyone on your municipality.
        </p>
        {thread}
      </div>
    );
  }

  return (
    <GrantOpportunitySection
      title="Team comments"
      description="Visible to everyone on your municipality team."
    >
      {thread}
    </GrantOpportunitySection>
  );
}
